import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../../../utils/request";

// Create async thunk for fetching templates
export const fetchTemplates = createAsyncThunk(
  "templates/fetchTemplates",
  async (page = 1, { rejectWithValue, getState }) => {
    try {
      // Get current state to validate page before making request
      const state = getState();
      const { templatesCount } = state.templates;
      const templatesPerPage = 10; // Should match the component value
      const totalPages = Math.ceil(templatesCount / templatesPerPage);

      // If requesting a page that doesn't exist and we have templates, redirect to last valid page
      if (page > totalPages && totalPages > 0) {
        const data = await request(
          `${process.env.REACT_APP_API_URL}/section-templates/?page=${totalPages}`,
          { method: "GET" }
        );
        return { ...data, redirectedFromPage: page, actualPage: totalPages };
      }

      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/?page=${page}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch templates");
    }
  }
);

// Create async thunk for fetching narratives
export const fetchNarratives = createAsyncThunk(
  "templates/fetchNarratives",
  async (page = 1, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/narrative-templates/?page=${page}`,
        { method: "GET" }
      );

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch narratives");
    }
  }
);

// Create async thunk for submitting software request
export const submitSoftwareRequest = createAsyncThunk(
  "templates/submitSoftwareRequest",
  async (message, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/templates/format-request/`,
        {
          method: "POST",
          body: JSON.stringify({ request: message }),
        }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.error || "Failed to submit software request"
      );
    }
  }
);

// Create async thunk for uploading XLS file
export const uploadXlsFile = createAsyncThunk(
  "templates/uploadXlsFile",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const url = `${process.env.REACT_APP_API_URL}/templates/upload-xls/`;

      const data = await request(url, {
        method: "POST",
        body: formData,
        headers: {}, // Explicitly pass empty headers to trigger FormData handling
      });

      return { ...data, fileName: file.name };
    } catch (error) {
      return rejectWithValue(error.error || "Failed to upload file");
    }
  }
);

// Create async thunk for checking task status
export const checkTaskStatus = createAsyncThunk(
  "templates/checkTaskStatus",
  async (taskId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/templates/task-status/${taskId}/`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.error || "Failed to check task status");
    }
  }
);

// Create async thunk for deleting/archiving template
export const deleteTemplate = createAsyncThunk(
  "templates/deleteTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/${templateId}/archive/`,
        { method: "POST" }
      );
      return { templateId, ...data };
    } catch (error) {
      return rejectWithValue(error.error || "Failed to delete template");
    }
  }
);

// Create async thunk for setting template as default
export const setTemplateAsDefault = createAsyncThunk(
  "templates/setTemplateAsDefault",
  async (templateId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/${templateId}/set-default/`,
        { method: "POST" }
      );
      return { templateId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.error || "Failed to set template as default"
      );
    }
  }
);

// Create async thunk for fetching template details
export const fetchTemplateDetails = createAsyncThunk(
  "templates/fetchTemplateDetails",
  async (templateId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/${templateId}/`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.error || "Failed to fetch template details");
    }
  }
);

// Create async thunk for renaming template
export const renameTemplate = createAsyncThunk(
  "templates/renameTemplate",
  async ({ templateId, name }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/${templateId}/`,
        {
          method: "PATCH",
          body: JSON.stringify({ name }),
        }
      );
      return { templateId, name, ...data };
    } catch (error) {
      return rejectWithValue(error.error || "Failed to rename template");
    }
  }
);

// Create async thunk for saving template sections
export const saveTemplateSections = createAsyncThunk(
  "templates/saveTemplateSections",
  async ({ templateId, sections }, { rejectWithValue }) => {
    try {
      // Transform sections to API format
      const apiSections = {
        sections: sections.map((section) => ({
          name: section.name,
          subsections: section.subsections
            ? section.subsections.map((subsection) => ({
                name: subsection.name,
              }))
            : [],
        })),
      };

      const data = await request(
        `${process.env.REACT_APP_API_URL}/section-templates/${templateId}/create-version/`,
        {
          method: "POST",
          body: JSON.stringify({ sections: apiSections }),
        }
      );
      return { templateId, sections: sections, apiResponse: data };
    } catch (error) {
      return rejectWithValue(error.error || "Failed to save template sections");
    }
  }
);

// Define initial state
const initialState = {
  templates: [],
  templatesCount: 0,
  getTemplatesStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  getTemplatesError: null,
  currentTemplatePage: 1,
  shouldRefetchTemplates: false,
  narratives: {
    count: 0,
    results: [],
  },
  getNarrativesStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  getNarrativesError: null,
  currentNarrativePage: 1,
  submitSoftwareRequestStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  submitSoftwareRequestError: null,
  uploadedFiles: [], // Array of uploaded files with their status
  uploadXlsFileStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  uploadXlsFileError: null,
  deleteTemplateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  deleteTemplateError: null,
  setDefaultStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  setDefaultError: null,
  // Template details state
  templateDetails: null,
  fetchTemplateDetailsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  fetchTemplateDetailsError: null,
  renameTemplateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  renameTemplateError: null,
  saveTemplateSectionsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
  saveTemplateSectionsError: null,
};

const templatesSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    setCurrentNarrativePage: (state, action) => {
      state.currentNarrativePage = action.payload;
    },
    setCurrentTemplatePage: (state, action) => {
      state.currentTemplatePage = action.payload;
    },
    clearSoftwareRequestStatus: (state) => {
      state.submitSoftwareRequestStatus = "idle";
      state.submitSoftwareRequestError = null;
    },
    addUploadedFile: (state, action) => {
      state.uploadedFiles.push(action.payload);
    },
    updateFileStatus: (state, action) => {
      const {
        fileId,
        status,
        error,
        templateId,
        narratives_from_microservice,
        narratives_duplicates_skipped,
      } = action.payload;
      const file = state.uploadedFiles.find((f) => f.id === fileId);
      if (file) {
        file.status = status;
        if (error) file.error = error;
        if (templateId) file.templateId = templateId;
        if (narratives_from_microservice !== undefined)
          file.narratives_from_microservice = narratives_from_microservice;
        if (narratives_duplicates_skipped !== undefined)
          file.narratives_duplicates_skipped = narratives_duplicates_skipped;
      }
    },
    clearUploadErrors: (state) => {
      state.uploadXlsFileStatus = "idle";
      state.uploadXlsFileError = null;
    },
    clearDeleteStatus: (state) => {
      state.deleteTemplateStatus = "idle";
      state.deleteTemplateError = null;
    },
    clearSetDefaultStatus: (state) => {
      state.setDefaultStatus = "idle";
      state.setDefaultError = null;
    },
    clearUploadedFiles: (state) => {
      state.uploadedFiles = [];
    },
    clearTemplateDetails: (state) => {
      state.templateDetails = null;
      state.fetchTemplateDetailsStatus = "idle";
      state.fetchTemplateDetailsError = null;
    },
    clearRenameStatus: (state) => {
      state.renameTemplateStatus = "idle";
      state.renameTemplateError = null;
    },
    clearSaveStatus: (state) => {
      state.saveTemplateSectionsStatus = "idle";
      state.saveTemplateSectionsError = null;
    },
    clearRefetchFlag: (state) => {
      state.shouldRefetchTemplates = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.getTemplatesStatus = "loading";
        state.getTemplatesError = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.getTemplatesStatus = "success";

        // Handle page redirection case
        if (action.payload.redirectedFromPage) {
          state.currentTemplatePage = action.payload.actualPage;
        }

        // Handle both mock data (array) and real API response (paginated object)
        if (Array.isArray(action.payload)) {
          // Mock data - simulate server-side pagination
          const itemsPerPage = 10;
          const startIndex = (state.currentTemplatePage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          state.templates = action.payload.slice(startIndex, endIndex);
          state.templatesCount = action.payload.length;
        } else {
          // Real API response with pagination
          state.templates = action.payload.results || action.payload.data || [];
          state.templatesCount =
            action.payload.count || action.payload.total || 0;
        }

        // Add mock data for testing - remove when real API is available
        if (state.templates.length === 0) {
          state.templates = [
            {
              id: 1,
              name: "my_template_1",
              created_at: "2025-05-01T00:00:00Z",
              updated_at: "2025-05-05T00:00:00Z",
              is_user_default: true,
              is_system_default: false,
            },
            {
              id: 2,
              name: "Standard Sections",
              created_at: "2025-04-15T00:00:00Z",
              updated_at: "2025-04-15T00:00:00Z",
              is_user_default: false,
              is_system_default: true,
            },
          ];
          state.templatesCount = 2;
        }
        state.getTemplatesError = null;
        state.shouldRefetchTemplates = false;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.getTemplatesStatus = "failed";
        state.getTemplatesError = action.payload;
        // Keep templates as empty array on failure
        state.templates = [];
        state.templatesCount = 0;
      })
      .addCase(fetchNarratives.pending, (state) => {
        state.getNarrativesStatus = "loading";
        state.getNarrativesError = null;
      })
      .addCase(fetchNarratives.fulfilled, (state, action) => {
        state.getNarrativesStatus = "success";
        state.narratives = action.payload;
        state.getNarrativesError = null;
      })
      .addCase(fetchNarratives.rejected, (state, action) => {
        state.getNarrativesStatus = "failed";
        state.getNarrativesError = action.payload;
        state.narratives = { count: 0, results: [] };
      })
      .addCase(submitSoftwareRequest.pending, (state) => {
        state.submitSoftwareRequestStatus = "loading";
        state.submitSoftwareRequestError = null;
      })
      .addCase(submitSoftwareRequest.fulfilled, (state, action) => {
        state.submitSoftwareRequestStatus = "success";
        state.submitSoftwareRequestError = null;
      })
      .addCase(submitSoftwareRequest.rejected, (state, action) => {
        state.submitSoftwareRequestStatus = "failed";
        state.submitSoftwareRequestError = action.payload;
      })
      // Upload XLS file reducers
      .addCase(uploadXlsFile.pending, (state) => {
        state.uploadXlsFileStatus = "loading";
        state.uploadXlsFileError = null;
      })
      .addCase(uploadXlsFile.fulfilled, (state, action) => {
        state.uploadXlsFileStatus = "success";
        state.uploadXlsFileError = null;
      })
      .addCase(uploadXlsFile.rejected, (state, action) => {
        state.uploadXlsFileStatus = "failed";
        state.uploadXlsFileError = action.payload;
      })
      // Check task status reducers
      .addCase(checkTaskStatus.fulfilled, (state, action) => {
        // If task is successful, mark that templates should be refetched
        if (action.payload.status === "SUCCESS") {
          state.shouldRefetchTemplates = true;
        }
      })
      .addCase(checkTaskStatus.rejected, (state, action) => {
        // Handle task status check errors if needed
        // Error is handled by component
      })
      // Delete template reducers
      .addCase(deleteTemplate.pending, (state) => {
        state.deleteTemplateStatus = "loading";
        state.deleteTemplateError = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.deleteTemplateStatus = "success";
        state.deleteTemplateError = null;
        // Remove the deleted template from the templates array
        state.templates = state.templates.filter(
          (template) => template.id !== action.payload.templateId
        );
        // Decrease the templates count
        state.templatesCount = Math.max(0, state.templatesCount - 1);
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.deleteTemplateStatus = "failed";
        state.deleteTemplateError = action.payload;
      })
      // Set template as default reducers
      .addCase(setTemplateAsDefault.pending, (state) => {
        state.setDefaultStatus = "loading";
        state.setDefaultError = null;
      })
      .addCase(setTemplateAsDefault.fulfilled, (state, action) => {
        state.setDefaultStatus = "success";
        state.setDefaultError = null;
        // Update the template to mark it as default
        const templateId = action.payload.templateId;
        state.templates.forEach((template) => {
          if (template.id === templateId) {
            template.is_user_default = true;
          } else {
            template.is_user_default = false;
          }
        });
      })
      .addCase(setTemplateAsDefault.rejected, (state, action) => {
        state.setDefaultStatus = "failed";
        state.setDefaultError = action.payload;
      })
      // Fetch template details reducers
      .addCase(fetchTemplateDetails.pending, (state) => {
        state.fetchTemplateDetailsStatus = "loading";
        state.fetchTemplateDetailsError = null;
      })
      .addCase(fetchTemplateDetails.fulfilled, (state, action) => {
        state.fetchTemplateDetailsStatus = "success";
        state.templateDetails = action.payload;
        state.fetchTemplateDetailsError = null;
      })
      .addCase(fetchTemplateDetails.rejected, (state, action) => {
        state.fetchTemplateDetailsStatus = "failed";
        state.fetchTemplateDetailsError = action.payload;
        state.templateDetails = null;
      })
      // Rename template reducers
      .addCase(renameTemplate.pending, (state) => {
        state.renameTemplateStatus = "loading";
        state.renameTemplateError = null;
      })
      .addCase(renameTemplate.fulfilled, (state, action) => {
        state.renameTemplateStatus = "success";
        state.renameTemplateError = null;
        // Update template details if it exists
        if (state.templateDetails) {
          state.templateDetails.name = action.payload.name;
        }
        // Update template in the templates list if it exists
        const template = state.templates.find(
          (t) => t.id === action.payload.templateId
        );
        if (template) {
          template.name = action.payload.name;
        }
      })
      .addCase(renameTemplate.rejected, (state, action) => {
        state.renameTemplateStatus = "failed";
        state.renameTemplateError = action.payload;
      })
      // Save template sections reducers
      .addCase(saveTemplateSections.pending, (state) => {
        state.saveTemplateSectionsStatus = "loading";
        state.saveTemplateSectionsError = null;
      })
      .addCase(saveTemplateSections.fulfilled, (state, action) => {
        state.saveTemplateSectionsStatus = "success";
        state.saveTemplateSectionsError = null;
        // Update template details sections if it exists
        if (state.templateDetails) {
          // If API returns updated template details, use it
          if (action.payload.apiResponse?.current_version) {
            state.templateDetails.current_version =
              action.payload.apiResponse.current_version;
          } else {
            // Otherwise update the sections in the current structure
            if (
              state.templateDetails.current_version &&
              action.payload.sections
            ) {
              state.templateDetails.current_version.sections = {
                sections: action.payload.sections.map((section) => ({
                  name: section.name,
                  subsections: section.subsections
                    ? section.subsections.map((subsection) => ({
                        name: subsection.name,
                      }))
                    : [],
                })),
              };
            }
          }
        }
      })
      .addCase(saveTemplateSections.rejected, (state, action) => {
        state.saveTemplateSectionsStatus = "failed";
        state.saveTemplateSectionsError = action.payload;
      });
  },
});

export const {
  setCurrentNarrativePage,
  setCurrentTemplatePage,
  clearSoftwareRequestStatus,
  addUploadedFile,
  updateFileStatus,
  clearUploadErrors,
  clearDeleteStatus,
  clearSetDefaultStatus,
  clearUploadedFiles,
  clearTemplateDetails,
  clearRenameStatus,
  clearSaveStatus,
  clearRefetchFlag,
} = templatesSlice.actions;
export default templatesSlice.reducer;
